// The map's sheets: the docked city sheet (village/info → SPY / PLAYER / GUILD / BOOKMARK / RESOURCES and ATTACK, or SEND
// SUPPORT + GO TO MY CITY for your own city), the player and guild sheets it opens, Bookmarks, Layers, and the city
// switcher that recentres the map.
import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/api/client";
import type { Village } from "@/api/types";
import { useCityActions } from "@/state/actions";
import { ownVillages, useInit } from "@/state/queries";
import { titleName } from "@/game/titles";
import { TitleBadge } from "../profile/TitleBadge";
import { bookmarkList, useBookmarkMutations, useBookmarks, useGuildDetails, usePlayerInfo, useVillageInfo, type GuildBrief } from "@/state/mapQueries";
import { useSession } from "@/state/session";
import { useMarkBook, useSaveMarks } from "@/state/marks";
import { MARK_NAME_MAX, applyMarkBook, bookOf, emptyMarkBook, moveItem, type Mark } from "@/game/markbook";
import { usePrompts } from "@/state/prompts";
import { Button, Disc, Empty, Medallion, Row, RowText, cx } from "@/kit/primitives";
import { Confirm, Sheet } from "@/kit/overlays";
import { CityPickList } from "@/kit/CityPick";
import { Toggle } from "@/kit/city";
import { IconBookmark, IconCart, IconChevronLeft, IconCheck, IconClock, IconClose, IconCrosshair, IconEye, IconPeople, IconPencil, IconPerson, IconScroll, IconTrash } from "@/kit/icons";
import { marksAt, marksWarning, openTools, useVtMarks, useVtOn } from "@/state/vt";
import { art } from "@/kit/art";
import type { Cell } from "@/map/chunks";
import { BARBARIAN_MARK, REL_COLORS, type Relation } from "@/map/relations";
import type { Layers } from "@/map/layers";
import { levelOf, owned } from "@/game/city";
import { fmtCoords, fmtNum, parseCoords, plural } from "@/game/format";
import { cleanName } from "./MapScreen";

export interface SheetTarget {
  x: number;
  y: number;
  cell: Cell | null;
}

// One row (Ben, Oct 14 2026): no distance, no march time and no city type any more; the points on one line; the five
// actions as icon squares on the same row as the two buttons. The icons keep their names for screen readers and as a
// hover title; Spy's count rides on its square.
export function CitySheet({ wid, vid, v, target, relation, guilds, bookmarked, onClose, onGo }: {
  wid: number; vid: number; v: Village; target: SheetTarget; home: [number, number] | null; relation: Relation;
  guilds: Map<number, GuildBrief> | undefined; bookmarked: boolean; onClose: () => void; onGo: (cell: [number, number]) => void;
}) {
  const nav = useNavigate();
  const session = useSession();
  const info = useVillageInfo(wid, [target.x, target.y]);
  const marks = useBookmarkMutations(wid);
  const actions = useCityActions(wid, vid);
  const [confirmSpy, setConfirmSpy] = useState(false);
  const [player, setPlayer] = useState<number | null>(null);
  const [guild, setGuild] = useState<number | null>(null);
  const d = info.data?.city_details?.[`${target.x},${target.y}`];
  const tv = d?.target_village;
  const tc = d?.target_char;
  const isOwn = !!tc && tc.user_id === session.uid;
  const isHere = tv?.id === vid;
  const isBarb = !!tv?.is_barbarian || !!target.cell?.barbarian;
  const spies = owned(v, "reveal_city");
  const hasMarket = levelOf(v, "b_m") > 0;
  const gd = useGuildDetails(wid, vid, tc && tc.gid > 0 ? tc.gid : null);
  const guildName = tc && tc.gid > 0 ? tc.guild_info?.name ?? guilds?.get(tc.gid)?.name : undefined;
  const guildTag = tc && tc.gid > 0 ? gd.data?.guild_info?.tag ?? guilds?.get(tc.gid)?.tag : undefined;
  const colors = REL_COLORS[relation];
  // the owner's worn title badge, as the live app's map popover shows it (MapPopoverScript; Ben, Oct 20 2026); tapping it
  // opens the player's profile, where their titles are
  const details = useInit().data?.titles_config?.details;
  const worn = tc && !isBarb && Number(tc.titles?.privacy) !== 1 && tc.titles?.title_main?.title ? { title: String(tc.titles.title_main.title), tier: Number(tc.titles.title_main.tier) || 1 } : null;
  const name = cleanName(tv?.city_name) || (isBarb ? "Barbarian city" : `(${target.x}, ${target.y})`);
  const go = (mode: "a" | "s") => nav(`/w/${wid}/city/${vid}/rally/commands?x=${target.x}&y=${target.y}${mode === "s" ? "&mode=s" : ""}`);
  // RESOURCES (Ben, Oct 13 2026): this city's Market › Send with the tapped city filled in as the target
  const sendRes = () => nav(`/w/${wid}/city/${vid}/market/send?x=${target.x}&y=${target.y}`);
  const toggleBookmark = () => {
    const coords = `${target.x},${target.y}`;
    if (bookmarked) marks.remove.mutate(coords, { onSuccess: () => usePrompts.getState().toast("Bookmark removed.") });
    else marks.add.mutate(coords, { onSuccess: () => usePrompts.getState().toast("Bookmarked.") });
  };
  const spy = () => actions.run("spy", () => api.rally.revealCity({ wid, vid }, tv!.id), { success: "Spy report written — see Reports." });
  // Valor Tools (Oct 15 2026; only for players Valor Tools' Admin page switched Valor Web on for): the Planner's land-time
  // finder at this city, adding it to the Planner, and the guild's claim / op on it
  const vtOn = useVtOn(wid);
  const vtMarks = useVtMarks(wid);
  const [tools, setTools] = useState(false);
  const vtNote = vtOn ? marksWarning(marksAt(vtMarks.data, target.x, target.y), !isOwn) : [];
  const nm = encodeURIComponent(cleanName(tv?.city_name) || "");
  const toTools = (to: string) => { setTools(false); openTools(wid, to, vtOn); };

  return (
    <div className={cx("map-sheet card", vtOn && "has-vt")} role="dialog" aria-label={`${name} at (${target.x}, ${target.y})`}>
      <button type="button" className="map-sheet-close" onClick={onClose} aria-label="Close"><IconClose /></button>
      {/* Valor Tools (switched on for the player): beside Close, so the one-row actions stay one row */}
      {vtOn && <button type="button" className="map-sheet-vtbtn" onClick={() => setTools(true)} aria-label="Valor Tools: land time, Planner" title="Valor Tools: who can land here, add to my Planner"><IconCrosshair /></button>}
      <div className="map-sheet-head">
        <Medallion src={art.med("b_hq")} alt="" size={40} className={cx("keepmed", relation)} />
        <div className="who">
          <h2>{info.isLoading && !tv ? "…" : name}</h2>
          <div className="owner">
            {isBarb ? <span className="muted">Barbarian · no one holds it</span> : tc ? (
              <>
                {worn && (
                  <button type="button" className="map-owner-badge" onClick={() => { onClose(); nav(`/w/${wid}/player/${tc.user_id}`); }} aria-label={`${tc.login} wears ${titleName(worn.title, details)} — open their profile`} title={titleName(worn.title, details)}>
                    <TitleBadge title={worn.title} tier={worn.tier} details={details} />
                  </button>
                )}
                {tc.login}{tc.gid > 0 && <> · <span style={{ color: colors.body ?? "var(--g-text)" }}>{guildTag ? `[${guildTag}] ` : ""}{guildName ?? ""}</span></>}
              </>
            ) : info.isError ? <span className="crimtxt">No city here.</span> : "…"}
          </div>
          <div className="coords">
            ({target.x}, {target.y}){tv ? <> · <b className="gold">R{tv.continent}</b></> : null}
            {isOwn ? <> · {isHere ? "this city" : "your city"}</> : relation !== "unaligned" && relation !== "barbarian" ? <> · {colors.label.toLowerCase()}</> : null}
            {target.cell?.beginnerProtection ? <> · <span className="bptag">protected</span></> : null}
            {target.cell?.truce ? <> · <span className="bptag">truce</span></> : null}
          </div>
        </div>
      </div>
      {vtNote.length > 0 && <div className="map-sheet-vt">{vtNote.map((w, i) => <p key={i} className={/^Claimed/.test(w) ? "crimtxt" : "gold"}>{w}</p>)}</div>}
      <div className="map-sheet-pts">
        <span><b>{tv ? fmtNum(tv.points) : "—"}</b> city pts</span>
        {!isBarb && <span><b>{tc ? fmtNum(tc.total_points) : "—"}</b> player pts</span>}
      </div>
      <div className="map-sheet-row">
        <div className="map-sheet-acts">
          <SheetAction icon={<IconEye />} label="Spy" disabled={!tv || isOwn || spies <= 0} hint={spies > 0 ? `${spies} left` : "none owned"} count={spies > 0 ? spies : undefined} onClick={() => setConfirmSpy(true)} />
          <SheetAction icon={<IconPerson />} label="Player" disabled={!tc || isBarb} onClick={() => setPlayer(tc!.user_id)} />
          <SheetAction icon={<IconPeople />} label="Guild" disabled={!tc || tc.gid <= 0} onClick={() => setGuild(tc!.gid)} />
          <SheetAction icon={<IconBookmark />} label={bookmarked ? "Saved" : "Bookmark"} on={bookmarked} busy={marks.add.isPending || marks.remove.isPending} onClick={toggleBookmark} />
          <SheetAction icon={<IconCart />} label="Resources" disabled={!tv || isHere || isBarb || !hasMarket} hint={tv && !isHere && !isBarb && !hasMarket ? "no market" : undefined} onClick={sendRes} />
        </div>
        <div className="map-sheet-primary">
          {isOwn ? (
            <>
              <Button kind="steel" size="sm" className="support" disabled={isHere} onClick={() => go("s")} aria-label="Send support">Support</Button>
              <Button kind="steel" size="sm" onClick={() => { if (tv && tv.id !== vid) session.setVillage(tv.id); nav(`/w/${wid}/city/${tv?.id ?? vid}`); }} aria-label="Go to my city">My city</Button>
            </>
          ) : (
            <>
              <Button kind="steel" size="sm" className="support" disabled={!tv || isBarb} onClick={() => go("s")} aria-label="Send support">Support</Button>
              <Button kind="crimson" size="sm" disabled={!tv} onClick={() => go("a")}>Attack</Button>
            </>
          )}
        </div>
      </div>
      <Confirm
        open={confirmSpy}
        onClose={() => setConfirmSpy(false)}
        title="Instant spy"
        text={<>Our instant spies are rare and valuable. Expend one on <b>{name}</b>? You have {plural(spies, "spy", "spies")}.</>}
        action="Send instant spy"
        kind="gold"
        busy={actions.isBusy("spy")}
        onConfirm={() => { setConfirmSpy(false); spy(); }}
      />
      <PlayerSheet wid={wid} uid={player} onClose={() => setPlayer(null)} onGo={(c) => { setPlayer(null); onGo(c); }} />
      <GuildSheet wid={wid} vid={vid} gid={guild} onClose={() => setGuild(null)} />
      {vtOn && (
        <Sheet open={tools} onClose={() => setTools(false)} label="Valor Tools" title={<h3 className="eyebrow" style={{ margin: "0 0 14px" }}>Valor Tools</h3>}>
          <div className="rows vt-tools">
            <Row as="button" onClick={() => toTools(`/app/?w=${wid}&fx=${target.x},${target.y}${nm ? `&fn=${nm}` : ""}${isOwn ? "&fs=1" : ""}`)}>
              <Disc size="sm" label=""><IconClock /></Disc>
              <RowText line={<b>Who can land here</b>} meta={`The Planner's land-time finder at ${name} (${target.x}, ${target.y})`} />
            </Row>
            <Row as="button" onClick={() => toTools(`/app/?w=${wid}&target=${target.x},${target.y}${nm ? `&name=${nm}` : ""}`)}>
              <Disc size="sm" label=""><IconScroll /></Disc>
              <RowText line={<b>Add to my Planner</b>} meta="A target in the operation on screen; set its landing time there" />
            </Row>
          </div>
        </Sheet>
      )}
    </div>
  );
}

/** An icon square: its name (with any hint) is the accessible name and the hover title; `count` rides on its corner. */
function SheetAction({ icon, label, hint, count, disabled, on, busy, onClick }: { icon: ReactNode; label: string; hint?: string; count?: number; disabled?: boolean; on?: boolean; busy?: boolean; onClick: () => void }) {
  const full = hint ? `${label} (${hint})` : label;
  return (
    <button type="button" className={cx("sheet-act", on && "on")} disabled={disabled || busy} onClick={onClick} aria-label={full} title={full}>
      <span className="ico">{busy ? <span className="spin" /> : icon}</span>
      {count !== undefined && <span className="hint" aria-hidden="true">{fmtNum(count)}</span>}
    </button>
  );
}

// ------------------------------------------------------------------ player
export function PlayerSheet({ wid, uid, onClose, onGo }: { wid: number; uid: number | null; onClose: () => void; onGo: (cell: [number, number]) => void }) {
  const nav = useNavigate();
  const q = usePlayerInfo(wid, uid);
  const pd = uid ? q.data?.player_details?.[String(uid)]?.target_char : undefined;
  const st = q.data?.player_stats;
  const details = useInit().data?.titles_config?.details;
  const tt = q.data?.target_user?.titles;
  const worn = tt && Number(tt.privacy) !== 1 && tt.title_main?.title ? { title: String(tt.title_main.title), tier: Number(tt.title_main.tier) || 1 } : null;
  return (
    <Sheet open={uid !== null} onClose={onClose} label="Player" title={<h3 className="eyebrow" style={{ margin: "0 0 14px" }}>Player</h3>}>
      {q.isLoading && <Empty><span className="spin" /> Reading the player…</Empty>}
      {q.isError && <Empty>The player couldn't be read.</Empty>}
      {pd && (
        <>
          <div className="map-player-head">
            <h2>{pd.login}</h2>
            {worn && (
              <button type="button" className="map-player-title" onClick={() => { onClose(); nav(`/w/${wid}/player/${uid}`); }}>
                <TitleBadge title={worn.title} tier={worn.tier} details={details} />
                <span>{titleName(worn.title, details)}</span>
              </button>
            )}
            <div className="muted">{pd.gid > 0 && pd.guild_info ? `[${pd.guild_info.tag}] ${pd.guild_info.name}` : "No guild"} · {plural(pd.villages?.length ?? 0, "city", "cities")}</div>
          </div>
          {st && (
            <div className="map-sheet-stats four">
              <div><span className="k">Rank</span><b>#{fmtNum(st.rank)}</b></div>
              <div><span className="k">Points</span><b>{fmtNum(st.total_points)}</b></div>
              <div><span className="k">Kills</span><b>{fmtNum(st.kst)}</b></div>
              <div><span className="k">As attacker</span><b>{fmtNum(st.ksa)}</b></div>
            </div>
          )}
          <div className="c-section" style={{ marginTop: 12 }}>Cities</div>
          <div className="rows">
            {(pd.villages ?? []).map((c) => {
              const xy = parseCoords(c.coords);
              return (
                <Row key={c.id} as="button" onClick={() => xy && onGo(xy)}>
                  <Medallion src={art.med("b_hq")} alt="" size={44} />
                  <RowText line={cleanName(c.city_name)} meta={`${fmtCoords(c.coords)} · R${c.continent} · ${fmtNum(c.points)} pts`} />
                </Row>
              );
            })}
          </div>
          <div style={{ marginTop: 14 }}>
            <Button kind="steel" size="md" wide onClick={() => { onClose(); nav(`/w/${wid}/player/${uid}`); }}>Full profile · titles, kill score, write</Button>
          </div>
        </>
      )}
    </Sheet>
  );
}

// ------------------------------------------------------------------ guild
export function GuildSheet({ wid, vid, gid, onClose }: { wid: number; vid: number; gid: number | null; onClose: () => void }) {
  const nav = useNavigate();
  const q = useGuildDetails(wid, vid, gid);
  const g = q.data?.guild_info;
  const mine = !!g && (g.guild_details ?? []).some((o) => (o as { is_members?: boolean }).is_members === true);
  const rows = (g?.guild_details ?? []).flatMap((o) => Object.entries(o).filter(([k]) => k !== "is_members"));
  return (
    <Sheet open={gid !== null} onClose={onClose} label="Guild" title={<h3 className="eyebrow" style={{ margin: "0 0 14px" }}>Guild</h3>}>
      {q.isLoading && <Empty><span className="spin" /> Reading the guild…</Empty>}
      {q.isError && <Empty>The guild couldn't be read.</Empty>}
      {g && (
        <>
          <div className="map-player-head">
            <h2>[{g.tag}] {g.name}</h2>
            {g.description && <div className="muted">{g.description}</div>}
          </div>
          <div className="map-kv">
            {rows.map(([k, val]) => <div key={k} className="kvrow"><span className="k">{k}</span><b>{String(val)}</b></div>)}
          </div>
          {/* no diplomacy here: from the map, a guild's summary doesn't show its allies, pacts or enemies (Ben, Oct 12 2026) */}
          <div style={{ marginTop: 14 }}>
            <Button kind="steel" size="md" wide onClick={() => { onClose(); nav(mine ? `/w/${wid}/guild` : `/w/${wid}/rank?show=guilds`); }}>{mine ? "Open your guild hall" : "Guild rankings"}</Button>
          </div>
        </>
      )}
    </Sheet>
  );
}

// ------------------------------------------------------------------ bookmarks
// Edit (Ben, Oct 13 2026): an Edit button turns the list into an editor — drag a row by its handle (or Up / Down on the
// focused handle) to reorder, type over a name to rename it (empty goes back to the city's name), tick rows and Delete
// to remove several at once. Delete goes to the game straight away (after a confirm); the names and order are saved to
// the Worker's KV (src/game/markbook.ts) on Done, and Cancel drops them. Recent targets aren't editable.
interface DraftMark { coords: string; city: string; name: string }

export function BookmarksSheet({ open, onClose, wid, onGo }: { open: boolean; onClose: () => void; wid: number; onGo: (cell: [number, number]) => void }) {
  const q = useBookmarks(wid, open);
  const book = useMarkBook(wid, open);
  const marks = useBookmarkMutations(wid);
  const saveBook = useSaveMarks(wid);
  const saved = applyMarkBook(bookmarkList(q.data?.bookmarks), book.data ?? emptyMarkBook());
  const named = new Map(saved.map((m) => [m.coords, m]));
  const recent = bookmarkList(q.data?.recent_bookmarks).map((b) => named.get(b.coords) ?? { coords: b.coords, city: b.name, name: b.name, renamed: false });
  const [pending, setPending] = useState<string | null>(null);

  // the editor
  const [draft, setDraft] = useState<DraftMark[] | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [asking, setAsking] = useState(false);
  const [dragging, setDragging] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const editing = draft !== null;
  const startEdit = () => {
    setDraft(saved.map((m) => ({ coords: m.coords, city: m.city, name: m.renamed ? m.name : "" })));
    setPicked(new Set());
  };
  const stopEdit = () => {
    setDraft(null);
    setPicked(new Set());
    setDragging(null);
  };
  const close = () => {
    stopEdit();
    onClose();
  };
  const before = bookOf(saved.map((m) => ({ coords: m.coords, city: m.city, name: m.name })));
  const after = draft ? bookOf(draft) : before;
  const dirty = JSON.stringify(before) !== JSON.stringify(after);
  const done = () => {
    if (!draft || !dirty) return stopEdit();
    saveBook.mutate(after, {
      onSuccess: () => {
        stopEdit();
        usePrompts.getState().toast("Bookmarks saved.", "success");
      },
      onError: (e) => usePrompts.getState().error(e),
    });
  };
  const move = (i: number, to: number) => setDraft((d) => (d ? moveItem(d, i, to) : d));
  const togglePick = (c: string) => setPicked((p) => {
    const n = new Set(p);
    if (n.has(c)) n.delete(c);
    else n.add(c);
    return n;
  });
  const allOn = !!draft?.length && draft.every((m) => picked.has(m.coords));
  const chosen = (draft ?? []).filter((m) => picked.has(m.coords));
  const deleteChosen = () => {
    const coords = chosen.map((m) => m.coords);
    marks.removeMany.mutate(coords, {
      onSuccess: () => {
        setDraft((d) => (d ? d.filter((m) => !coords.includes(m.coords)) : d));
        setPicked(new Set());
        setAsking(false);
        usePrompts.getState().toast(coords.length === 1 ? "Bookmark deleted." : `${coords.length} bookmarks deleted.`, "success");
      },
      onError: (e) => {
        setAsking(false);
        usePrompts.getState().error(e);
      },
    });
  };

  // drag by the handle: the row follows the finger, swapping places as it passes another row's middle
  const onHandleDown = (e: ReactPointerEvent<HTMLButtonElement>, coords: string) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDragging(coords);
  };
  const onHandleMove = (e: ReactPointerEvent<HTMLButtonElement>) => {
    if (!dragging || !draft || !listRef.current) return;
    const rows = Array.from(listRef.current.querySelectorAll<HTMLElement>("[data-bm]"));
    const from = draft.findIndex((m) => m.coords === dragging);
    const to = rows.findIndex((r) => {
      const b = r.getBoundingClientRect();
      return e.clientY >= b.top && e.clientY < b.bottom;
    });
    if (to >= 0 && to !== from) move(from, to);
  };
  const onHandleUp = () => setDragging(null);
  const onHandleKey = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    if (e.key === "ArrowUp" && i > 0) {
      e.preventDefault();
      move(i, i - 1);
    } else if (e.key === "ArrowDown" && draft && i < draft.length - 1) {
      e.preventDefault();
      move(i, i + 1);
    }
  };
  // after a keyboard move, keep the focus on the handle that moved
  const focusAfter = useRef<string | null>(null);
  useEffect(() => {
    if (!focusAfter.current || !listRef.current) return;
    listRef.current.querySelector<HTMLButtonElement>(`[data-bm="${focusAfter.current}"] .bm-grip`)?.focus();
    focusAfter.current = null;
  });

  const row = (b: Mark, deletable: boolean) => {
    const xy = parseCoords(b.coords);
    return (
      <Row key={`${deletable ? "s" : "r"}${b.coords}`} className="bmrow">
        <button type="button" className="rowhit" onClick={() => xy && onGo(xy)} aria-label={`Go to ${b.name}`}>
          <IconBookmark style={{ width: 18, height: 18, color: "var(--g-text)", flex: "none" }} />
          <RowText line={b.name} meta={b.renamed ? `${b.city} · ${fmtCoords(b.coords)}` : fmtCoords(b.coords)} />
        </button>
        {deletable && (
          <Disc size="xs" label={`Remove bookmark ${b.name}`} className="bmdel" disabled={pending === b.coords} onClick={() => { setPending(b.coords); marks.remove.mutate(b.coords, { onSettled: () => setPending(null) }); }}>
            <IconTrash />
          </Disc>
        )}
      </Row>
    );
  };

  const editRow = (m: DraftMark, i: number) => {
    const on = picked.has(m.coords);
    const label = m.name.trim() || m.city;
    return (
      <div key={m.coords} data-bm={m.coords} className={cx("bm-edit", on && "on", dragging === m.coords && "dragging")}>
        <button type="button" className={cx("bm-check", on && "on")} role="checkbox" aria-checked={on} aria-label={`Select ${label}`} onClick={() => togglePick(m.coords)}>
          {on && <IconCheck />}
        </button>
        <div className="bm-namebox">
          <input className="input bm-name" value={m.name} placeholder={m.city} maxLength={MARK_NAME_MAX} aria-label={`Name for ${m.city} ${fmtCoords(m.coords)}`}
            onChange={(e) => { const name = e.target.value; setDraft((d) => (d ? d.map((x) => (x.coords === m.coords ? { ...x, name } : x)) : d)); }}
            onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }} />
          <div className="meta">{m.name.trim() && m.name.trim() !== m.city ? `${m.city} · ` : ""}{fmtCoords(m.coords)}</div>
        </div>
        <button type="button" className="bm-grip" aria-label={`Move ${label}`} title="Drag to reorder (or Up / Down)"
          onPointerDown={(e) => onHandleDown(e, m.coords)} onPointerMove={onHandleMove} onPointerUp={onHandleUp} onPointerCancel={onHandleUp}
          onKeyDown={(e) => { focusAfter.current = m.coords; onHandleKey(e, i); }}>
          <span aria-hidden="true" />
        </button>
      </div>
    );
  };

  return (
    <Sheet open={open} onClose={close} from="header" label="Bookmarks"
      title={
        <div className="bm-head">
          {/* Back (Ben, Oct 15 2026): to the map; while editing it cancels the edit first, as Cancel does */}
          <Disc size="sm" className="bm-back" label={editing ? "Back to bookmarks" : "Back to the map"} onClick={editing ? stopEdit : close}><IconChevronLeft /></Disc>
          <h3 className="eyebrow">Bookmarks</h3>
          {editing ? (
            <span className="bm-head-acts">
              <Button size="sm" kind="steel" onClick={stopEdit} disabled={saveBook.isPending}>Cancel</Button>
              <Button size="sm" kind="gold" onClick={done} busy={saveBook.isPending}>Done</Button>
            </span>
          ) : (
            saved.length > 0 && <Button size="sm" kind="steel" onClick={startEdit} disabled={book.isFetching && book.isPlaceholderData}><IconPencil /> Edit</Button>
          )}
        </div>
      }>
      {q.isLoading && <Empty><span className="spin" /> Reading bookmarks…</Empty>}
      {editing ? (
        <>
          {draft.length > 0 ? (
            <>
              <div className="c-section">Saved<span className="aside">Drag ≡ to reorder · tap a name to rename</span></div>
              <div className="rows bm-edits" ref={listRef}>{draft.map(editRow)}</div>
              <div className="card-foot bm-foot">
                <button type="button" className="link caps" onClick={() => setPicked(allOn ? new Set() : new Set(draft.map((m) => m.coords)))}>{allOn ? "Clear" : "Select all"}</button>
                <Button size="sm" kind="crimson" disabled={!chosen.length} onClick={() => setAsking(true)}>
                  <IconTrash /> Delete{chosen.length ? ` (${chosen.length})` : ""}
                </Button>
              </div>
            </>
          ) : (
            <Empty>No bookmarks left.</Empty>
          )}
          <Confirm open={asking} onClose={() => setAsking(false)} title={chosen.length === 1 ? "Delete this bookmark?" : `Delete ${chosen.length} bookmarks?`}
            text={chosen.length === 1 ? `${chosen[0].name.trim() || chosen[0].city} comes off your bookmarks.` : `${plural(chosen.length, "bookmark", "bookmarks")} come off your list.`}
            action="Delete" busy={marks.removeMany.isPending} onConfirm={deleteChosen} />
        </>
      ) : (
        <>
          {!q.isLoading && !saved.length && !recent.length && <Empty>No bookmarks yet. Tap a city on the map and choose Bookmark.</Empty>}
          {saved.length > 0 && <div className="c-section">Saved</div>}
          <div className="rows">{saved.map((b) => row(b, true))}</div>
          {recent.length > 0 && <div className="c-section" style={{ marginTop: 14 }}>Recent targets</div>}
          <div className="rows">{recent.map((b) => row(b, false))}</div>
        </>
      )}
    </Sheet>
  );
}

// ------------------------------------------------------------------ layers
// (Oct 2026: no "Territory washes" switch — the Slate & Fire castles carry their relationship in roofs, cloth and pennant;
// map/layers.ts drops the old stored setting.)
export function LayersSheet({ open, onClose, layers, onChange, guildMarks = false }: { open: boolean; onClose: () => void; layers: Layers; onChange: (l: Layers) => void; guildMarks?: boolean }) {
  const set = (k: keyof Layers) => (on: boolean) => onChange({ ...layers, [k]: on });
  return (
    <Sheet open={open} onClose={onClose} label="Layers" title={<h3 className="eyebrow" style={{ margin: "0 0 14px" }}>Layers</h3>}>
      <div className="rows">
        <Row><RowText line="All nameplates" meta="Every city's name under its castle (off: no names)" /><div className="right"><Toggle on={layers.allNames} onChange={set("allNames")} label="All nameplates" /></div></Row>
        <Row><RowText line="Forests and terrain" meta="Where the world's noise puts trees, lakes and mountains" /><div className="right"><Toggle on={layers.forest} onChange={set("forest")} label="Forests" /></div></Row>
        <Row><RowText line="Surveyed grid" meta="The faint survey grid over the ground" /><div className="right"><Toggle on={layers.grid} onChange={set("grid")} label="Grid" /></div></Row>
        <Row><RowText line="Marches" meta="Your armies on the move, with their landing times" /><div className="right"><Toggle on={layers.marches} onChange={set("marches")} label="Marches" /></div></Row>
        <Row><RowText line="Animated armies" meta="Walking squads on the marches (off: arrows only; lighter on an older phone)" /><div className="right"><Toggle on={layers.armies} onChange={set("armies")} label="Animated armies" /></div></Row>
        <Row><RowText line="Guilds on the map" meta="The list of guilds when zoomed out to the realm (off: a small Guilds button)" /><div className="right"><Toggle on={layers.guildLegend} onChange={set("guildLegend")} label="Guilds on the map" /></div></Row>
        {guildMarks && <Row><RowText line="Guild marks" meta="Valor Tools: your guild's claims (red, yours gold) and guild op targets (gold ring)" /><div className="right"><Toggle on={layers.guildMarks} onChange={set("guildMarks")} label="Guild marks" /></div></Row>}
      </div>
      <div className="map-legend-key">
        {(["you", "guild", "ally", "nap", "enemy", "unaligned", "barbarian"] as Relation[]).map((r) => (
          <span key={r} className="key"><span className="dot" style={{ background: REL_COLORS[r].body ?? BARBARIAN_MARK }} />{REL_COLORS[r].label}</span>
        ))}
      </div>
    </Sheet>
  );
}

// ------------------------------------------------------------------ the city switcher (recentres the map)
export function MapCitySwitcher({ open, onClose, v, wid, vid, onPick }: { open: boolean; onClose: () => void; v: Village | undefined; wid: number; vid: number | null; onPick: (c: { id: number; coords: string }) => void }) {
  const session = useSession();
  const cities = ownVillages(v, session.uid);
  return (
    <Sheet open={open} onClose={onClose} from="top" anchor=".whdr-bar" label="Choose a city" title={<h3 className="eyebrow" style={{ margin: "0 0 14px" }}>Your cities · World {wid}</h3>}>
      <CityPickList wid={wid} cities={cities} current={vid} empty="No cities yet." onPick={(c) => onPick(c)} />
    </Sheet>
  );
}
